import React, { memo, useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import { Form, Button, Input, Checkbox } from "antd";

// containers
import SelectFieldAxios from "containers/SelectFieldAxios";
// components
import CustomSelect from "components/Forms/Fields/CustomSelect";

// global user
import { useChangeChildByID } from "engine/Contexts/User.context";

// utils
import {
  DISPENSARY_PROGRAM_OPTIONS,
  ACTIVITY_REASON_OPTIONS,
} from "utils/helpers/helper.constant";
import { getStatusesList } from "utils/helpers/helper.generateData";
import { sortByKey, clean } from "utils/helpers/helper.general";
// hooks
import { useRefState } from "utils/hooks";
import _local from "utils/localStorageUtils";

function ReasonForm({
  formData,
  type,
  isEdit,
  isNew,
  // actions
  nextStep,
  prevStep,
  result,
  setSelectedStudent,
  // default
  className,
}) {
  const childClassName = classNames("form-absence", className);
  const { t, i18n } = useTranslation();

  const localUser = _local.getLocalUser();
  const changeChildByID = useChangeChildByID();

  const [isDisabled, setIsDisabled] = useState(false);

  const [isChildAbs, setIsChildAbs] = useState(
    !isNew && (!!isEdit?.origin || !!formData?.origin)
  );

  // only for teacher mode
  const [studentParent, studentParentRef, setStudentParent] = useRefState(
    false,
  );

  const [absenceStatus, setAbsenceStatus] = useState(
    formData?.status || "pending",
  );
  const initFormData = {};
  const [AbsFormReason] = Form.useForm();

  if (localUser?.role == "teacher") {
    initFormData.status = "pending";
  }

  /*
   * Check current form data from parent
   * Set old data to the form
   */
  useEffect(() => {
    if (formData) {
      const hasValFormData = clean(formData);
      AbsFormReason.setFieldsValue({
        status: hasValFormData?.status || "pending",
        reason: hasValFormData?.reason || null,
        unjustified: hasValFormData?.unjustified || false,
        student:
          hasValFormData?.student?._id || hasValFormData?.student || null,
      });
      setAbsenceStatus(hasValFormData?.status || "pending");
      setIsDisabled(false);

      if (hasValFormData?.student?._id) {
        localUser?.role == "parent" &&
          changeChildByID(
            hasValFormData?.student?._id || hasValFormData?.student,
          );
        const contact_person =
          hasValFormData?.student?.contact_person?._id ||
          hasValFormData?.student?.contact_person;
        const parent =
          contact_person ||
          hasValFormData?.student?.parents?.[0]?._id ||
          hasValFormData?.student?.parents?.[0];
        setStudentParent(parent);
      }

      if (!isNew && !!formData?.origin) {
        setIsChildAbs(!!formData?.origin);
      }
    } else {
      setIsChildAbs(false);
    }
  }, [formData]);

  const onFinish = values => {
    const resultObj = values || {};
    resultObj.parent = studentParentRef?.current || null;
    result(resultObj);
    !!nextStep && nextStep();
    AbsFormReason.resetFields();
  };

  

  const getReasons = useCallback(() => {
    const DEFAULT_REASONS =
      type == "medical" ? DISPENSARY_PROGRAM_OPTIONS() : ACTIVITY_REASON_OPTIONS();
    const sortBy = "label";
    const listReasons = DEFAULT_REASONS.map(elem => {
      return {
        key: elem?.key,
        label: elem?.label,
        title: elem?.title || elem?.label,
        value: elem?.value,
      };
    });
    // sort options
    return listReasons.sort(sortByKey(sortBy));
  }, [i18n?.language]);

  // Select student for current absence
  let selectStudents = useMemo(() => {
    // initial data for SelectFieldAxios component
    const newElementSelect = {
      label: t("labels.student"),
      name: "student",
      endponit: `students/list/select`,
      endponitSend: {},
      disabled: !!isChildAbs,
      required: true,
      value: formData?.student?._id || formData?.student || "",
      displayLabel: true,
      type: "axios-select",
      tagRender: props => (
        <span className="multiple-label-list">{props.label}</span>
      ),
      onSelect: (LabeledValue, option) => {
        setSelectedStudent(option?.item);
        localUser?.role == "parent" &&
          changeChildByID(option?.item?._id || option?.item);
        const contact_person =
          option?.item?.contact_person?._id || option?.item?.contact_person;
        const parent =
          contact_person ||
          option?.item?.parents[0]?._id ||
          option?.item?.parents[0];
        setStudentParent(parent);
      },
      itemCallback: item => {
        return {
          key: item?._id,
          label: `${item?.description?.lastname} ${
            item?.description?.firstname
          }`,
          value: item?._id,
          item: item,
        };
      },
    };

    return (
      <>
        <SelectFieldAxios
          isSearch={true}
          sortBy="label"
          className="axios-select"
          placeholder={t("inputs.selectStudent")}
          tagRender={newElementSelect.tagRender}
          itemCallback={newElementSelect.itemCallback}
          item={newElementSelect}
          onSelect={newElementSelect.onSelect}
          endponit={newElementSelect.endponit}
          endponitSend={newElementSelect.endponitSend}
          disabled={!!isChildAbs}
          /*  getResponse={response => {
            if (response?.data?.length > 0) {}
          }} */
        />
        {studentParentRef?.current && (
          <Form.Item
            name="parent"
            hidden={true}
            initialValue={studentParentRef.current}
            rules={[
              {
                required: false,
                message: "",
              },
            ]}
          >
            <Input />
          </Form.Item>
        )}
      </>
    );
  }, [
    formData,
    studentParentRef,
    setStudentParent,
    setSelectedStudent,
    localUser,
    isChildAbs,
    i18n?.language
  ]);

  const getStatuses = useCallback(absenceType => getStatusesList(absenceType), [i18n?.language]);

  return (
    <div className={childClassName}>
      <Form
        form={AbsFormReason}
        className="form-absence__form"
        name={`form_absence_${type}_reason`}
        initialValues={initFormData}
        onFinish={onFinish}
        onValuesChange={values => {
          setIsDisabled(false);
          values?.status && setAbsenceStatus(values?.status);
        }}
      >
        {localUser?.role == "teacher" && selectStudents}

        {localUser?.role == "teacher" && (
          <CustomSelect
            className={`accent-label select-status ${absenceStatus}`}
            label={t("common.status")}
            value={absenceStatus}
            placeholder={t("inputs.select")}
            name={`status`}
            options={getStatuses(type)}
          />
        )}
        
        {localUser?.role == "teacher" && (
          <>
            <Form.Item
              name="unjustified"
              key="unjustified"
              className="forms-field forms-field--checkbox"
              valuePropName="checked"
            >
              <Checkbox
                disabled={formData?.type == "unjustified"}
                checked={
                  formData?.type == "unjustified" || !!formData?.unjustified
                }
              >
                <span className="checkbox-label">{t("absences.unjustifiedAbsence")}</span>
              </Checkbox>
            </Form.Item>
          </>
        )}

        {/* INFO: hide this filed for v2.0.0 */}
        {type != "medical" && (
          <CustomSelect
            className="accent-label"
            label={`${t("absences.reason")} : ${
              type == "medical" ? t("global.absenceTypes.medical") : t("global.absenceTypes.no-medical")
            }`}
            placeholder={t("inputs.select")}
            name={`reason`}
            options={getReasons()}
          />
        )}

        <div className="absence-actions-wrapper">
          <Button
            className="btn-previous"
            onClick={() => !!prevStep && prevStep()}
          >
            {t("common.previous")}
          </Button>
          <Button
            className="next-btn"
            form={`form_absence_${type}_reason`}
            htmlType="submit"
            disabled={isDisabled}
          >
            {t("common.next")}
          </Button>
        </div>
      </Form>
    </div>
  );
}

ReasonForm.defaultProps = {
  type: "medical",
};

ReasonForm.propTypes = {
  type: PropTypes.oneOf(["medical", "no-medical", "sickness", "unjustified"]).isRequired,
  result: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
};

export default memo(ReasonForm);
